import { useCallback, useContext, useEffect, useRef } from 'preact/hooks'
import { AthosBeacon } from 'libs/athos/classes/athos-beacon'
import { createContext } from 'preact'
import { useAthosContext } from 'libs/athos/fetchers/provider'
import { useIntersectionObserver } from '@/components/hooks/useIntersectionObserver'

const defaultValue = {
  analytics: undefined,
  responseId: undefined
}

const BeaconAnalyticsContext = createContext(defaultValue)

export function useBeaconAnalytics() {
  const context = useContext(BeaconAnalyticsContext)
  return context ?? defaultValue
}

/**
 * Provides a Beacon 2.0 tracker for a surface (search, autocomplete, category or
 * recommendations). Must be rendered inside the relevant Athos results provider so
 * it can read the `responseId` returned by the API — every Beacon 2.0 event
 * references it.
 */
export function BeaconAnalyticsProvider({ type = 'recommendations', profile, children }) {
  const { current: analytics } = useRef(new AthosBeacon(type, profile))
  const { results } = useAthosContext()
  const responseId = results?.responseId

  // Assigned synchronously during render, not inside a useEffect: child hooks
  // firing on this same commit would otherwise read a stale (usually absent)
  // responseId and the event would silently never post.
  if (responseId) analytics.setResponseId(responseId)

  const value = { analytics, responseId }

  return <BeaconAnalyticsContext.Provider value={value}>{children}</BeaconAnalyticsContext.Provider>
}

/**
 * Fires a single `render` event once the surface has results and a responseId.
 * Payload: { data: { tag, responseId } }.
 *
 * Keyed on the current `responseId` rather than a one-shot boolean: a
 * refinement (new query, filter, sort, page) produces a new `responseId` and
 * must re-report.
 */
export function useBeaconLogRender({ enabled = true } = {}) {
  const { analytics, responseId } = useBeaconAnalytics()
  const { results } = useAthosContext()
  const lastFired = useRef(null)

  useEffect(() => {
    if (!enabled || !analytics) return
    if (!responseId || !results?.results?.length) return
    if (lastFired.current === responseId) return
    lastFired.current = responseId
    analytics.render()
  }, [analytics, enabled, responseId, results?.results?.length])
}

/**
 * Fires an `impression` event when the surface scrolls into view.
 *
 * Beacon 2.0 defaults to per-card impressions at a 70% visibility threshold, but
 * personalization surfaces are explicitly permitted to batch the visible array in
 * a single impression — which is what we do here (one event for the block).
 *
 * Keyed on the current `responseId` rather than a one-shot boolean, same
 * reasoning as `useBeaconLogRender`.
 */
export function useBeaconLogImpressions(ref, { enabled = true } = {}) {
  const { analytics, responseId } = useBeaconAnalytics()
  const { results } = useAthosContext()
  const { setNode, entry } = useIntersectionObserver({ threshold: 0.1 })
  const lastFired = useRef(null)

  useEffect(() => {
    if (ref) setNode(ref)
  }, [ref])

  useEffect(() => {
    if (!enabled || !analytics) return
    if (!entry?.isIntersecting) return
    if (!responseId || !results?.results?.length) return
    if (lastFired.current === responseId) return
    lastFired.current = responseId
    analytics.impression(results.results)
  }, [analytics, enabled, entry, responseId, results?.results?.length])
}

/**
 * Returns a handler that fires a `clickthrough` event for the clicked result.
 * Uses `sendBeacon`/`keepalive` so the event survives the navigation.
 */
export function useBeaconLogClick() {
  const { analytics } = useBeaconAnalytics()

  return useCallback((result) => {
    if (!analytics) return
    analytics.clickthrough(result)
  }, [analytics])
}
